import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API_CONFIG } from '../../config/api';
import {
  ArrowLeft,
  Star,
  Clock,
  Users,
  CheckCircle,
  Zap,
  Globe,
  Palette,
  Smartphone,
  Server,
  Megaphone,
  TrendingUp,
  Award,
  Shield,
  MessageSquare,
  Calendar,
  DollarSign,
  Eye,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Layers,
  ArrowRight
} from 'lucide-react';
import ConsultationForm from '../ConsultationForm';
import MediaDisplay from '../MediaDisplay';

// Map string icon names from API to actual components
const iconMap = {
  Globe,
  Palette,
  Smartphone,
  Server,
  Megaphone,
  Zap,
  Users,
  TrendingUp,
  Star,
  Award,
  Shield,
  Calendar,
  DollarSign,
  Eye,
  Download,
  ExternalLink,
  MessageSquare
};

const CategoryDetailPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  // Kategori verileri
  const categories = {
    'ecommerce-projects': {
      id: 'ecommerce-projects',
      name: 'E-ticaret Projeleri',
      icon: Globe,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      count: '25+ Proje',
      trend: '+40%',
      description: 'Modern e-ticaret çözümleri ile online satışlarınızı artırın. Güvenli ödeme sistemleri, stok yönetimi ve müşteri deneyimi odaklı tasarımlar.',
      features: [
        'Güvenli Ödeme Sistemleri',
        'Stok Yönetimi',
        'Müşteri Yönetim Paneli',
        'SEO Optimizasyonu',
        'Mobil Uyumlu Tasarım',
        'Analitik ve Raporlama'
      ],
      subcategories: [
        { id: 'moda-magazasi', name: 'Moda Mağazası', count: '8 Proje', price: '₺15.000-50.000', description: 'Trendy ve kullanıcı dostu moda e-ticaret siteleri' },
        { id: 'elektronik-magaza', name: 'Elektronik Mağaza', count: '6 Proje', price: '₺20.000-80.000', description: 'Teknik özelliklerin öne çıktığı elektronik mağazaları' },
        { id: 'gida-icecek', name: 'Gıda & İçecek', count: '5 Proje', price: '₺12.000-35.000', description: 'Taze ürün odaklı gıda e-ticaret çözümleri' },
        { id: 'kozmetik-magaza', name: 'Kozmetik Mağaza', count: '6 Proje', price: '₺18.000-45.000', description: 'Görsel odaklı kozmetik ve güzellik mağazaları' }
      ]
    },
    'web-projects': {
      id: 'web-projects',
      name: 'Web Tasarım Projeleri',
      icon: Palette,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      count: '40+ Proje',
      trend: '+35%',
      description: 'Profesyonel web tasarımı ile markanızı dijital dünyada öne çıkarın. Responsive, hızlı ve SEO uyumlu web siteleri.',
      features: [
        'Responsive Tasarım',
        'SEO Optimizasyonu',
        'Hızlı Yükleme',
        'Modern UI/UX',
        'İçerik Yönetim Sistemi',
        'Güvenlik Önlemleri'
      ],
      subcategories: [
        { id: 'kurumsal-web-sitesi', name: 'Kurumsal Web Sitesi', count: '15 Proje', price: '₺5.000-25.000', description: 'Profesyonel kurumsal kimlik yansıtan web siteleri' },
        { id: 'portfolyo-sitesi', name: 'Portfolyo Sitesi', count: '12 Proje', price: '₺3.000-15.000', description: 'Yaratıcılığınızı sergileyen portfolyo siteleri' },
        { id: 'blog-haber-sitesi', name: 'Blog & Haber Sitesi', count: '8 Proje', price: '₺4.000-18.000', description: 'İçerik odaklı blog ve haber siteleri' },
        { id: 'landing-page', name: 'Landing Page', count: '5 Proje', price: '₺2.000-8.000', description: 'Dönüşüm odaklı landing page tasarımları' }
      ]
    },
    'mobile-projects': {
      id: 'mobile-projects',
      name: 'Mobil Uygulama Projeleri',
      icon: Smartphone,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      count: '18+ Proje',
      trend: '+50%',
      description: 'iOS ve Android platformları için native ve cross-platform mobil uygulamalar. Kullanıcı deneyimi odaklı tasarım.',
      features: [
        'Native iOS/Android',
        'Cross-Platform Geliştirme',
        'Push Notification',
        'Offline Çalışma',
        'Güvenli API Entegrasyonu',
        'App Store Optimizasyonu'
      ],
      subcategories: [
        { id: 'ios-uygulamasi', name: 'iOS Uygulaması', count: '8 Proje', price: '₺25.000-100.000', description: 'Apple App Store için native iOS uygulamaları' },
        { id: 'android-uygulamasi', name: 'Android Uygulaması', count: '7 Proje', price: '₺20.000-80.000', description: 'Google Play Store için native Android uygulamaları' },
        { id: 'cross-platform', name: 'Cross-Platform', count: '3 Proje', price: '₺30.000-120.000', description: 'React Native ile çoklu platform uygulamaları' }
      ]
    },
    'ai-projects': {
      id: 'ai-projects',
      name: 'AI & Otomasyon Projeleri',
      icon: Zap,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      count: '12+ Proje',
      trend: '+60%',
      description: 'Yapay zeka ve otomasyon teknolojileri ile iş süreçlerinizi optimize edin. Chatbot, analiz ve otomasyon çözümleri.',
      features: [
        'Chatbot Geliştirme',
        'Makine Öğrenmesi',
        'Veri Analizi',
        'İş Süreci Otomasyonu',
        'Doğal Dil İşleme',
        'Görüntü Tanıma'
      ],
      subcategories: [
        { id: 'chatbot-gelistirme', name: 'Chatbot Geliştirme', count: '5 Proje', price: '₺8.000-30.000', description: 'Müşteri hizmetleri için akıllı chatbot çözümleri' },
        { id: 'ai-analiz-sistemi', name: 'AI Analiz Sistemi', count: '4 Proje', price: '₺15.000-50.000', description: 'Veri analizi ve tahminleme sistemleri' },
        { id: 'otomasyon-sistemi', name: 'Otomasyon Sistemi', count: '3 Proje', price: '₺10.000-40.000', description: 'İş süreçlerini otomatikleştiren sistemler' }
      ]
    },
    'hosting-projects': {
      id: 'hosting-projects',
      name: 'Hosting & Altyapı Projeleri',
      icon: Server,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50',
      count: '30+ Proje',
      trend: '+25%',
      description: 'Güvenli, hızlı ve ölçeklenebilir hosting çözümleri. VPS, cloud migration ve güvenlik hizmetleri.',
      features: [
        'VPS Kurulumu',
        'Cloud Migration',
        'SSL Sertifikaları',
        'Güvenlik Duvarı',
        'Yedekleme Sistemleri',
        '7/24 Teknik Destek'
      ],
      subcategories: [
        { id: 'vps-kurulumu', name: 'VPS Kurulumu', count: '15 Proje', price: '₺2.000-10.000', description: 'Özel sanal sunucu kurulum ve yapılandırması' },
        { id: 'cloud-migration', name: 'Cloud Migration', count: '8 Proje', price: '₺5.000-20.000', description: 'Bulut platformlarına geçiş ve optimizasyon' },
        { id: 'ssl-guvenlik', name: 'SSL & Güvenlik', count: '7 Proje', price: '₺1.000-5.000', description: 'Güvenlik sertifikaları ve güvenlik önlemleri' }
      ]
    },
    'marketing-projects': {
      id: 'marketing-projects',
      name: 'Dijital Pazarlama Projeleri',
      icon: Megaphone,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      count: '20+ Proje',
      trend: '+30%',
      description: 'Dijital pazarlama stratejileri ile markanızı büyütün. SEO, sosyal medya ve reklam kampanyaları.',
      features: [
        'SEO Optimizasyonu',
        'Sosyal Medya Yönetimi',
        'Google Ads',
        'İçerik Pazarlama',
        'E-mail Marketing',
        'Analitik ve Raporlama'
      ],
      subcategories: [
        { id: 'seo-optimizasyonu', name: 'SEO Optimizasyonu', count: '8 Proje', price: '₺3.000-12.000', description: 'Arama motoru optimizasyonu ve organik trafik artırma' },
        { id: 'sosyal-medya-yonetimi', name: 'Sosyal Medya Yönetimi', count: '7 Proje', price: '₺2.000-8.000', description: 'Sosyal medya hesapları yönetimi ve içerik üretimi' },
        { id: 'google-ads-kampanyasi', name: 'Google Ads Kampanyası', count: '5 Proje', price: '₺4.000-15.000', description: 'Hedefli reklam kampanyaları ve dönüşüm optimizasyonu' }
      ]
    }
  };

  // Örnek proje verileri
  const sampleProjects = {
    'ecommerce-projects': [
      {
        id: 1,
        name: 'Fashion Store Pro',
        description: 'Modern moda mağazası e-ticaret sitesi. Responsive tasarım ve güvenli ödeme sistemi.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺35.000',
        duration: '6 hafta',
        rating: 4.9,
        reviews: 24,
        features: ['Responsive Tasarım', 'Güvenli Ödeme', 'Stok Yönetimi', 'SEO Optimizasyonu'],
        status: 'Tamamlandı'
      },
      {
        id: 2,
        name: 'TechHub Electronics',
        description: 'Elektronik ürünler için özel tasarlanmış e-ticaret platformu.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺45.000',
        duration: '8 hafta',
        rating: 4.8,
        reviews: 18,
        features: ['Teknik Özellik Tablosu', 'Karşılaştırma Modülü', 'Video Galerisi', 'Teknik Destek'],
        status: 'Tamamlandı'
      }
    ],
    'web-projects': [
      {
        id: 1,
        name: 'Corporate Website',
        description: 'Profesyonel kurumsal web sitesi. Modern tasarım ve hızlı yükleme.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺15.000',
        duration: '4 hafta',
        rating: 4.9,
        reviews: 32,
        features: ['Responsive Tasarım', 'CMS Entegrasyonu', 'SEO Optimizasyonu', 'Hızlı Yükleme'],
        status: 'Tamamlandı'
      }
    ],
    'mobile-projects': [
      {
        id: 1,
        name: 'Fitness Tracker App',
        description: 'iOS ve Android için fitness takip uygulaması.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺65.000',
        duration: '12 hafta',
        rating: 4.8,
        reviews: 28,
        features: ['Cross-Platform', 'Push Notification', 'Offline Çalışma', 'Sosyal Özellikler'],
        status: 'Tamamlandı'
      }
    ],
    'ai-projects': [
      {
        id: 1,
        name: 'Smart Chatbot',
        description: 'AI destekli müşteri hizmetleri chatbot sistemi.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺25.000',
        duration: '8 hafta',
        rating: 4.9,
        reviews: 15,
        features: ['Doğal Dil İşleme', 'Makine Öğrenmesi', 'API Entegrasyonu', 'Analitik'],
        status: 'Tamamlandı'
      }
    ],
    'hosting-projects': [
      {
        id: 1,
        name: 'Cloud Migration',
        description: 'AWS bulut platformuna geçiş ve optimizasyon.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺18.000',
        duration: '6 hafta',
        rating: 4.8,
        reviews: 22,
        features: ['AWS Migration', 'Güvenlik', 'Yedekleme', 'Monitoring'],
        status: 'Tamamlandı'
      }
    ],
    'marketing-projects': [
      {
        id: 1,
        name: 'SEO Campaign',
        description: 'Kapsamlı SEO optimizasyonu ve dijital pazarlama kampanyası.',
        image: 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli',
        price: '₺12.000',
        duration: '12 hafta',
        rating: 4.7,
        reviews: 18,
        features: ['SEO Optimizasyonu', 'İçerik Pazarlama', 'Analitik', 'Raporlama'],
        status: 'Tamamlandı'
      }
    ]
  };

  useEffect(() => {
    // Fade-in animasyonu için
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);

    // API'den güncel veriyi çek
    const fetchData = async () => {
      try {
        // API'den kategori verilerini çek
        const categoryResponse = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getProjectCategories`);
        
        if (!categoryResponse.ok) {
          throw new Error(`HTTP error! status: ${categoryResponse.status}`);
        }
        
        const categoryContentType = categoryResponse.headers.get('content-type');
        if (!categoryContentType || !categoryContentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }
        
        const categoryData = await categoryResponse.json();
        
        if (categoryData && Array.isArray(categoryData)) {
          // Önce slug ile ara
          let foundCategory = categoryData.find(cat => cat.slug === categoryId);
          
          // Eğer slug ile bulunamadıysa, id ile ara (geriye dönük uyumluluk için)
          if (!foundCategory) {
            foundCategory = categoryData.find(cat => cat.id === categoryId);
            
            // Eğer id ile bulunduysa ve slug varsa, URL'yi slug'a yönlendir
            if (foundCategory && foundCategory.slug) {
              navigate(`/kategori/${foundCategory.slug}`, { replace: true });
              return; // Redirect yapıldı, işlemi durdur
            }
          }
          
          if (foundCategory) {
            // Normalize icon if API returns a string name
            const normalized = {
              ...foundCategory,
              icon: typeof foundCategory.icon === 'string' 
                ? (iconMap[foundCategory.icon] || Globe)
                : foundCategory.icon
            };
            setCategory(normalized);
            
            // Projeleri kategori id'si ile çek
            const actualCategoryId = foundCategory.id;
            const projectsResponse = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getProjects&category_id=${actualCategoryId}`);
            
            if (projectsResponse.ok) {
              const projectsContentType = projectsResponse.headers.get('content-type');
              if (projectsContentType && projectsContentType.includes('application/json')) {
                const projectsData = await projectsResponse.json();
                if (Array.isArray(projectsData)) {
                  setProjects(projectsData);
                }
              }
            }
            return; // Kategori bulundu, işlem tamamlandı
          }
        }
        
        // Eğer kategori bulunamadıysa, eski kod ile devam et (geriye dönük uyumluluk)
        const projectsResponse = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getProjects&category_id=${categoryId}`);
        
        if (projectsResponse.ok) {
          const projectsContentType = projectsResponse.headers.get('content-type');
          if (projectsContentType && projectsContentType.includes('application/json')) {
            const projectsData = await projectsResponse.json();
            if (projectsData && Array.isArray(projectsData)) {
              setProjects(projectsData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching category data:', error);
        // Fallback zaten set edildi, hata durumunda değiştirme
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    return () => clearTimeout(timer);
  }, [categoryId, navigate]);

  // Kategori bulunamadı (loading check kaldırıldı)
  if (!category) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Kategori Bulunamadı</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = typeof category.icon === 'string' 
    ? (iconMap[category.icon] || Globe)
    : category.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Ana Sayfaya Dön
            </Link>
          </div>
          
          <div className="text-center space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-lg px-6 py-3 rounded-full text-lg font-medium">
                <IconComponent className="w-6 h-6" />
                <span>{category.name}</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                {category.name}
              </h1>
              
              <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        {/* Özellikler */}
        <div className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sol Taraf - Başlık ve Kartlar */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Neler Sunuyoruz?
                </h2>
                <p className="text-gray-600">
                  {category.name} için profesyonel çözümlerimiz
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {(category.features || []).map((feature, index) => {
                  // Handle both old string format and new object format
                  const featureName = typeof feature === 'string' ? feature : feature.name;
                  const featureDescription = typeof feature === 'string' 
                    ? `Profesyonel ${feature.toLowerCase()} hizmeti`
                    : feature.description;
                  
                  return (
                    <div key={index} className="bg-white p-4 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{featureName}</h3>
                          <p className="text-gray-600 text-sm">
                            {featureDescription}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sağ Taraf - Resim/Video */}
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl overflow-hidden">
                <MediaDisplay
                  src={category?.image_url || "https://placehold.co/600x600/e0e7ff/6366f1?text=Kategori+Görseli"}
                  alt={category.name}
                  className="w-full h-full object-cover"
                  videoProps={{ controls: true }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Alt Kategoriler */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Alt Kategoriler
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Bu kategorideki alt alanlara göz atın ve ihtiyacınıza uygun projeleri keşfedin.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.subcategories.map((subcategory, index) => {
              const subSlug = (subcategory.name || '')
                .toLowerCase()
                .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
                .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
                .replace(/&/g, 've').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              return (
                <Card key={index} className="group overflow-hidden border border-gray-200 hover:shadow-xl hover:border-gray-300 transition-all duration-300 h-full flex flex-col">
                  <div className={`h-1.5 bg-gradient-to-r ${category.color}`} />
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        <Layers className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-gray-800 mb-1">
                          {subcategory.name}
                        </h3>
                        <Badge variant="secondary" className="text-xs font-medium">
                          {subcategory.count ?? 'Projeler'}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">
                      {subcategory.description}
                    </p>
                    {subcategory.price && (
                      <p className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        {subcategory.price}
                      </p>
                    )}
                    <Link
                      to={`/kategori/${category?.slug || categoryId}/${subSlug}`}
                      className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors mt-auto"
                    >
                      Detayları Gör
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Örnek Projeler */}
        {projects.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Örnek Projeler
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => {
                // Parse features if it's a JSON string
                const projectFeatures = typeof project.features === 'string' 
                  ? JSON.parse(project.features || '[]')
                  : (project.features || []);
                
                // Translate status
                const statusMap = {
                  'completed': 'Tamamlandı',
                  'in_progress': 'Devam Ediyor',
                  'planned': 'Planlanıyor'
                };
                const statusText = statusMap[project.status] || project.status;
                
                return (
                  <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-shadow group">
                    <div className="aspect-video bg-gray-100 relative overflow-hidden">
                      <div className="w-full h-[120%] transform transition-transform duration-500 ease-out group-hover:-translate-y-[16.67%]">
                        <MediaDisplay
                          src={project.image_url || project.image || 'https://placehold.co/400x300/e0e7ff/6366f1?text=Proje+Görseli'}
                          alt={project.name}
                          className="w-full h-full object-cover"
                          videoProps={{ controls: true }}
                        />
                      </div>
                      <div className="absolute top-3 right-3 z-10">
                        <Badge className="bg-green-500 text-white">
                          {statusText}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {project.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {project.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {projectFeatures.slice(0, 2).map((feature, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-sm font-medium">{project.rating}</span>
                          <span className="text-sm text-gray-500">({project.reviews_count || project.reviews || 0})</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {project.duration}
                        </div>
                      </div>
                      
                      <div className="flex justify-end items-center gap-2">
                        {project.project_url && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(project.project_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Siteyi Gör
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          asChild
                        >
                          <Link to={`/project/${project.id || project.slug || project.name?.toLowerCase().replace(/\s+/g, '-')}`}>
                            <Eye className="w-4 h-4 mr-1" />
                            Detayları Gör
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            {category.name} Projenizi Başlatalım!
          </h2>
          <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
            Uzman ekibimizle birlikte {category.name.toLowerCase()} alanında hayalinizdeki projeyi gerçekleştirelim. Size özel çözümler üretiyor, projelerinizi başarıyla tamamlıyoruz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowConsultationForm(true)} className="bg-white text-purple-600 hover:bg-gray-100">
              <MessageSquare className="w-5 h-5 mr-2" />
              Ücretli Danışmanlık
            </Button>
            <Button onClick={() => { window.location.href = '/iletisim'; }} variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600">
              <Calendar className="w-5 h-5 mr-2" />
              İletişim
            </Button>
          </div>
        </div>
      </div>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative z-50 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <ConsultationForm isModal={true} onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryDetailPage;
